import React from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDeleteConfirmState } from '@/components/hooks';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, Edit2, Trash2, CheckCircle, Archive, ArchiveRestore, ThumbsUp, Award } from 'lucide-react';
import { format } from 'date-fns';

export default function CapabilityCard({ capability, officers, issues, policies, objectives, onEdit, isSeniorOfficer, onApprove, onInstitutionalize, onArchive, onUnarchive }) {
  const queryClient = useQueryClient();
  const deleteConfirm = useDeleteConfirmState({ confirmTimeout: 3000 });

  const deleteCapabilityMutation = useMutation({
    mutationFn: (id) => base44.entities.Capability.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['capabilities'] })
  });

  const activateCapabilityMutation = useMutation({
    mutationFn: (id) =>
      base44.entities.Capability.update(id, {
        status: 'active',
        actual_completion_date: new Date().toISOString().split('T')[0]
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    }
  });

  const requester = officers.find((o) => o.user_email === capability.requested_by);
  const sponsor = officers.find((o) => o.user_email === capability.sponsor_email);
  const linkedIssues = issues.filter((i) => capability.linked_issue_ids?.includes(i.id));
  const linkedPolicies = policies.filter((p) => capability.linked_policy_ids?.includes(p.id));
  const linkedObjective = objectives.find((o) => o.id === capability.linked_objective_id);

  const statusColors = {
    requested: 'bg-slate-500/20 text-slate-400',
    in_development: 'bg-blue-500/20 text-blue-400',
    contracted: 'bg-purple-500/20 text-purple-400',
    active: 'bg-emerald-500/20 text-emerald-400',
    deprecated: 'bg-red-500/20 text-red-400'
  };

  const typeColors = {
    contracted: 'bg-purple-500/20 text-purple-400',
    acquired: 'bg-blue-500/20 text-blue-400',
    developed: 'bg-indigo-500/20 text-indigo-400'
  };

  return (
    <Card className="glass-panel border-slate-800">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-emerald-400" />
              <CardTitle className="text-lg">{capability.name}</CardTitle>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge className={statusColors[capability.status]}>
                {capability.status.replace('_', ' ')}
              </Badge>
              <Badge className={typeColors[capability.type]}>
                {capability.type}
              </Badge>
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(capability)}>
              <Edit2 className="w-4 h-4" />
            </Button>
            {capability.status === 'in_development' && (
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-emerald-400"
                onClick={() => activateCapabilityMutation.mutate(capability.id)}
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
            )}
            {capability.status === 'deprecated' ? (
              onUnarchive && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-amber-400 hover:text-amber-300"
                  onClick={() => onUnarchive(capability.id)}
                  title="Undeprecate"
                >
                  <ArchiveRestore className="w-4 h-4" />
                </Button>
              )
            ) : (
              onArchive && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 hover:text-amber-400"
                  onClick={() => onArchive(capability.id)}
                  title="Deprecate"
                >
                  <Archive className="w-4 h-4" />
                </Button>
              )
            )}
            <div className="relative">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 hover:text-red-400"
                onClick={() => deleteConfirm.checkAndConfirm(capability.id, () => deleteCapabilityMutation.mutate(capability.id))}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              {deleteConfirm.isPendingConfirm(capability.id) && (
                <span className="absolute right-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                  Click again to confirm
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {capability.description && (
          <p className="text-sm text-slate-400">{capability.description}</p>
        )}

        <div className="space-y-2">
          {capability.system_tech_component && (
            <div>
              <p className="text-xs text-slate-500 font-semibold">System/Tech:</p>
              <p className="text-sm text-slate-300">{capability.system_tech_component}</p>
            </div>
          )}
          {capability.people_component && (
            <div>
              <p className="text-xs text-slate-500 font-semibold">People:</p>
              <p className="text-sm text-slate-300">{capability.people_component}</p>
            </div>
          )}
          {capability.process_component && (
            <div>
              <p className="text-xs text-slate-500 font-semibold">Process:</p>
              <p className="text-sm text-slate-300">{capability.process_component}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500">Requested by:</span>
            <span className="ml-2 text-slate-300">{requester?.callsign || capability.requested_by}</span>
          </div>
          {sponsor && (
            <div>
              <span className="text-slate-500">Sponsor:</span>
              <span className="ml-2 text-slate-300">{sponsor.callsign}</span>
            </div>
          )}
          {capability.target_completion_date && (
            <div>
              <span className="text-slate-500">Target date:</span>
              <span className="ml-2 text-slate-300">
                {format(new Date(capability.target_completion_date), 'MMM d, yyyy')}
              </span>
            </div>
          )}
        </div>

        {linkedIssues.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-2">Addresses Issues:</p>
            <div className="flex flex-wrap gap-1">
              {linkedIssues.map((issue) => (
                <Badge key={issue.id} variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">
                  {issue.title}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {linkedPolicies.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-2">Will Replace Policies:</p>
            <div className="flex flex-wrap gap-1">
              {linkedPolicies.map((policy) => (
                <Badge key={policy.id} variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                  {policy.title}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {linkedObjective && (
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-1">Linked Objective:</p>
            <Badge variant="outline" className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 text-xs">
              {linkedObjective.statement.substring(0, 50)}...
            </Badge>
          </div>
        )}

        {isSeniorOfficer && (capability.status === 'requested' || capability.status === 'draft') && onApprove && (
          <Button
            size="sm"
            className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onApprove(capability.id)}
          >
            <ThumbsUp className="w-3 h-3 mr-1" />
            Approve
          </Button>
        )}

        {isSeniorOfficer && capability.status === 'approved' && onInstitutionalize && (
          <Button
            size="sm"
            className="w-full h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
            onClick={() => onInstitutionalize(capability.id)}
          >
            <Award className="w-3 h-3 mr-1" />
            Institutionalize
          </Button>
        )}
      </CardContent>
    </Card>
  );
}